import type { createSession, getWorkspaceSettings, listWorkspaceAgents, CoworkSession } from "./api"
import type { selectDefaultCoworkAgent } from "./default-agent"
import { selectAssignedCoworkAgentByTemplate } from "./default-agent"
import type { openCoworkChat, CoworkChatTarget } from "./open-chat"
import type { queueReportTemplate } from "./report-template-request"
import type { useCoworkWorkspace } from "./workspace-context"
import { trialErrorKey } from "./trial-errors"

type Request = Parameters<typeof queueReportTemplate>[2]
export type ReportTemplateLaunchScope = Pick<Request, "companyID" | "projectID" | "periodID">
type Workspace = ReturnType<typeof useCoworkWorkspace>

export function acceptedReportTemplateScope(
  context: Pick<
    Workspace,
    "state" | "selectedProject" | "selectedPeriod" | "supportsPeriods" | "periods" | "allPeriodsID"
  >,
): ReportTemplateLaunchScope | undefined {
  const project = context.selectedProject()
  if (!project || project.id !== context.state.projectId || project.company_id !== context.state.entityId) return
  const scope = { companyID: project.company_id, projectID: project.id }
  if (context.state.periodId === context.allPeriodsID) return { ...scope, periodID: null }
  const period = context.selectedPeriod()
  if (
    period &&
    period.id === context.state.periodId &&
    period.company_id === scope.companyID &&
    period.project_id === scope.projectID
  )
    return { ...scope, periodID: period.id }
  if (!context.state.periodId && context.periods.state === "ready" && !context.supportsPeriods())
    return { ...scope, periodID: null }
}

export type ReportTemplateLaunchState = {
  busy: boolean
  retry: boolean
  templateID: string
  title: string
  notice?: "configuration" | "checkChats" | "openFailed" | "scopeChanged" | "createdNotSent"
  trialError?: ReturnType<typeof trialErrorKey>
}

type Intent = Pick<Request, "id" | "templateID" | "visiblePrompt" | "instructions" | "reportOutputLanguage"> & {
  title: string
  locale: string
}
type Attempt = {
  intent: Intent
  scope: ReportTemplateLaunchScope
  revision: number
  created?: CoworkSession
  uncertain?: boolean
}

export function createReportTemplateLauncher(deps: {
  createSession: typeof createSession
  listWorkspaceAgents: typeof listWorkspaceAgents
  getWorkspaceSettings: typeof getWorkspaceSettings
  selectDefaultAgent: typeof selectDefaultCoworkAgent
  open: (target: CoworkChatTarget, request: Request, isCurrent: () => boolean) => ReturnType<typeof openCoworkChat>
  onState: (state: ReportTemplateLaunchState) => void
}) {
  let scope: ReportTemplateLaunchScope | undefined
  let identity = ""
  let revision = 0
  let disposed = false
  let busy = false
  let attempt: Attempt | undefined
  let retryable = false

  const current = (value: Attempt) => !disposed && attempt === value && value.revision === revision
  function publish(notice?: ReportTemplateLaunchState["notice"], trialError?: ReportTemplateLaunchState["trialError"]) {
    if (disposed) return
    deps.onState({
      busy,
      retry: retryable,
      templateID: attempt?.intent.templateID ?? "",
      title: attempt?.intent.title ?? "",
      notice,
      trialError,
    })
  }

  async function run(value: Attempt) {
    // This lock is shared by every card and retry, before the first await.
    busy = true
    retryable = false
    publish()
    let stage: "configuration" | "create" | "open" = "configuration"
    let notice: ReportTemplateLaunchState["notice"]
    let trialError: ReportTemplateLaunchState["trialError"]
    let completed = false
    try {
      if (!current(value)) return
      if (!value.created) {
        const [agents, settings] = await Promise.all([deps.listWorkspaceAgents(), deps.getWorkspaceSettings()])
        if (!current(value)) return
        const agent = selectAssignedCoworkAgentByTemplate(agents, "accounting", value.scope, settings.default_agent_id)
        if (!agent) throw new Error("accounting-agent-unavailable")
        stage = "create"
        if (!current(value)) return
        const created = await deps.createSession(
          value.scope.projectID,
          value.intent.title,
          value.scope.periodID ?? undefined,
          { locale: value.intent.locale, application_locale: value.intent.locale },
          agent?.id,
          { type: "project" },
          value.scope.companyID,
        )
        if (!created?.id) throw new Error("unconfirmed-create")
        value.created = created
      }
      if (!current(value)) return
      stage = "open"
      const created = value.created
      const opened = await deps.open(
        {
          ...value.scope,
          sessionID: created.id,
          remoteSessionID: created.remote_opencode_session_id,
          title: created.title,
        },
        {
          id: value.intent.id,
          templateID: value.intent.templateID,
          ...value.scope,
          sessionID: created.id,
          visiblePrompt: value.intent.visiblePrompt,
          instructions: value.intent.instructions,
          reportOutputLanguage: value.intent.reportOutputLanguage,
        },
        () => current(value),
      )
      completed = !!opened
      if (!opened && current(value)) {
        retryable = true
        notice = "openFailed"
      }
    } catch (error) {
      trialError = trialErrorKey(error)
      // A rejected create may already have committed. Never retry it automatically.
      if (stage === "create" && !trialError) value.uncertain = true
      notice = trialError ? undefined : stage === "create" ? "checkChats" : stage === "open" ? "openFailed" : "configuration"
      retryable = !trialError && stage === "open" && current(value)
    } finally {
      busy = false
      if (!completed && !current(value)) {
        retryable = false
        notice = value.created ? "createdNotSent" : notice === "checkChats" ? notice : "scopeChanged"
      }
      publish(notice, trialError)
      if (completed) attempt = undefined
    }
  }

  return {
    setScope(next: ReportTemplateLaunchScope | undefined, nextIdentity: string) {
      scope = next && { ...next }
      if (identity === nextIdentity) return
      identity = nextIdentity
      revision++
      if (!attempt) return
      retryable = false
      publish(attempt.created ? "createdNotSent" : attempt.uncertain ? "checkChats" : "scopeChanged")
    },
    generate(intent: Intent) {
      if (busy || disposed || !scope) return Promise.resolve()
      attempt = { intent: { ...intent }, scope: { ...scope }, revision }
      return run(attempt)
    },
    retry() {
      if (busy || !retryable || !attempt?.created || !current(attempt)) return Promise.resolve()
      return run(attempt)
    },
    dispose() {
      disposed = true
      revision++
    },
  }
}
